// Search basics (TEAMBRIDGE_SEO_BASICS, default on; lib/seo-basics.js): the homepage and /pricing get a title,
// description and og tags written for what buyers search, and /pricing's first HTML has its text (passes,
// prices, FAQ). Shared by the server and the app, so both say the same thing. Plain ESM.
// Keywords from the competitor study (scratch own/competitors, 2026-10-24): "virtual team building games",
// "games to play on zoom with coworkers", "games to play in microsoft teams" (+64% a year), "icebreaker games
// for meetings". The old title ("Games For Remote Team Socials") matched no search.
import { passWindow } from '../../util/passWindow.js';
import { homepageCanonical } from './homepage-content.js';

// The server marks a page it rendered this way; the app keeps the same head tags only where it's there.
export const SEO_BASICS_MARKER = 'tb-seo-basics';
export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;
export const HOME_SEO = Object.freeze({
  title: 'Virtual Team Building Games for Teams & Zoom | TeamBridge',
  description: 'Icebreakers and team building games to play with coworkers in Microsoft Teams, Zoom or Meet. One link, ' + 'nothing to install, up to 200 players. Free to try.',
  ogTitle: 'Virtual Team Building Games for Microsoft Teams, Zoom and Meet'
});
const dollars = amount => `$${Number.isInteger(amount) ? amount : Number(amount).toFixed(2)}`;
const perPerson = (amount, people) => {
  const each = amount / people;
  return each >= 10 ? `$${Math.round(each)}` : `$${each.toFixed(2)}`;
};

// The two passes on sale (pass-only pricing), or null: the copy below needs both.
function passes(state) {
  const event = state?.passOnly && state.offers?.EVENT_PASS;
  const year = state?.passOnly && state.offers?.YEAR_PASS;
  return event?.amount && year?.amount ? {
    event,
    year
  } : null;
}

// /pricing's title and description from the offers the page sells (lib/pricing-catalog.js publicOffers).
export function pricingSeo(state) {
  const p = passes(state);
  if (!p) {
    return {
      title: 'Pricing: Team Games for Remote Teams | TeamBridge',
      description: 'One price for your whole team, not per person. Play icebreakers and team games in Microsoft Teams, Zoom or Meet, nothing to install.'
    };
  }
  const title = `Pricing: ${p.event.name || 'Team Event Pass'} ${dollars(p.event.amount)}, Year Pass ${dollars(p.year.amount)} | TeamBridge`;
  const lead = `One price for your whole team, not per person: ${dollars(p.event.amount)} for ${passWindow(p.event.accessDays)} ` + `or ${dollars(p.year.amount)} for ${passWindow(p.year.accessDays)}, up to ${p.event.members} players.`;
  const tail = ' No booking, nothing to install.';
  return {
    title: title.length <= TITLE_MAX ? title : `Pricing: ${dollars(p.event.amount)} and ${dollars(p.year.amount)} team passes | TeamBridge`,
    description: (lead + tail).length <= DESCRIPTION_MAX ? lead + tail : lead
  };
}

// The buyers' top complaints about the alternatives (per-person prices, booking ahead, player caps, installs,
// Zoom only), answered on /pricing, first in its FAQ. Numbers come from the offers.
export function pricingBuyerQuestions(state) {
  const p = passes(state);
  if (!p) return [];
  const {
    event
  } = p;
  return [{
    question: 'Is it priced per person?',
    answer: `No. One pass covers everyone in your lobbies, up to ${event.members} players. The ${dollars(event.amount)} ` + `${event.name || 'Team Event Pass'} works out at about ${perPerson(event.amount, 20)} a person for a team of 20, ` + `or ${perPerson(event.amount, event.members)} a person for ${event.members}.`
  }, {
    question: 'Do we have to book ahead?',
    answer: 'No. Buy a pass and start a game in the same minute. There is no host to schedule, no minimum headcount and nothing to wait for.'
  }, {
    question: 'How many people can play at once?',
    answer: `Up to ${event.members} players per pass. Draw Battle, Type Racer and Fake Artist take up to 200 at once; ` + 'Trivia, This or That and Scribble up to 100. Every game shows its player range before you start.'
  }, {
    question: 'Do players need to install anything or sign up?',
    answer: 'No. Players open your invite link in the browser on a laptop or phone and type a name. Only the host has an account.'
  }, {
    question: 'Does it work in Microsoft Teams and Google Meet, or only Zoom?',
    answer: 'Any of them, and Webex too. Keep talking in the call you already use while everyone plays in their own ' + 'browser tab: nothing to add to the meeting app and nothing to screen-share.'
  }];
}

// The head tags the server wrote, as the app keeps them: the homepage's, and the landing page's own (read from
// the document when this module loads, before Helmet changes anything, so /pricing keeps the title the server
// built from the live offers).
export function seoBasicsOn(doc = typeof document === 'undefined' ? null : document) {
  return !!doc?.querySelector?.(`meta[name="${SEO_BASICS_MARKER}"]`);
}
export function readLanding(doc) {
  if (!seoBasicsOn(doc)) return null;
  return {
    pathname: doc.defaultView?.location?.pathname || null,
    title: doc.title,
    description: doc.querySelector('meta[name="description"]')?.getAttribute('content') || '',
    url: doc.querySelector('meta[property="og:url"]')?.getAttribute('content') || null
  };
}
const LANDING = readLanding(typeof document === 'undefined' ? null : document);
export function seoHead(pathname, doc = typeof document === 'undefined' ? null : document, landing = LANDING) {
  if (!seoBasicsOn(doc)) return null;
  if (pathname === '/') return {
    ...HOME_SEO,
    url: homepageCanonical
  };
  if (!landing?.title || pathname !== landing.pathname) return null;
  return {
    title: landing.title,
    description: landing.description,
    ogTitle: landing.title,
    url: landing.url
  };
}